import Spinner from 'components/Loading/Spinner'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  IntegrationServices,
  useApiQueryClient,
  useMutation,
  useQuery,
  useRunInvalidationForMutation
} from '@sequencehq/api/utils'
import { Button, Skeleton, Box, Flex } from '@sequencehq/core-components'
import {
  GreyGrey10,
  GreyGrey60,
  GreyGrey80,
  Primary13SemiBold,
  Primary13Regular
} from '@sequencehq/core-components'
import { getIntegrationName } from 'Integrations/utils/getIntegrationName'
import { ExistingEntityLink } from '../LinkEntityWidget/ExistingEntityLink'
import { UnlinkCreditNoteModal } from './UnlinkCreditNoteModal'
import { useNotifications } from '@sequencehq/core-components'
import {
  dashboardv99990101Client,
  type Dashboardv99990101CommonModels
} from '@sequencehq/api/dashboard/v99990101'
import { match } from 'ts-pattern'
import { ProtectedButton } from 'lib/rbac'
import { getSyncErrorDescription } from 'Integrations/integrationsConfig/common/shared'

interface LinkedCreditNoteWidgetProps {
  creditNoteId: string
  service: IntegrationServices
}

type CreditNoteStatus =
  Dashboardv99990101CommonModels.CreditNotes.CreditNoteStatus

type SyncUnavailableReason = 'notIssued' | 'voided'

const useLinkedCreditNoteWidget = (props: {
  service: IntegrationServices
  creditNoteId: string
}) => {
  const serviceName = getIntegrationName(props.service)
  const { displayNotification } = useNotifications()
  const apiQueryClient = useApiQueryClient()

  const creditNoteQuery = useQuery(
    dashboardv99990101Client.getCreditNote,
    { id: props.creditNoteId },
    {
      select: creditNote => {
        if (!creditNote) {
          return null
        }

        const externalCreditNote = creditNote.linkedServices.find(
          ls => ls.externalService === props.service
        )

        return {
          customerId: creditNote.customerId,
          syncUnavailableReason: match<
            CreditNoteStatus,
            SyncUnavailableReason | null
          >(creditNote.status)
            .with('VOIDED', () => 'voided')
            .with('DRAFT', 'IN_PROGRESS', () => 'notIssued')
            .with('FINAL', 'SENT', () => null)
            .exhaustive(),
          linkedCreditNote: externalCreditNote
            ? {
                id: externalCreditNote.externalId,
                url: externalCreditNote.externalUrl
              }
            : null
        }
      }
    }
  )

  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    { id: creditNoteQuery.data?.customerId ?? '' },
    {
      enabled: Boolean(creditNoteQuery.data?.customerId),
      select: customer => {
        if (!customer) {
          return null
        }

        const customerLink = customer.integrationIds.find(
          int => int.service === props.service
        )

        return customerLink
      }
    }
  )

  const linkingMutation = useMutation(
    dashboard20240730Client.postPushCreditNoteToExternalService
  )
  const runMutationInvalidation = useRunInvalidationForMutation()
  const linkCreditNote = () => {
    linkingMutation.mutate(
      {
        creditNoteId: props.creditNoteId,
        service: props.service
      },
      {
        onError: error => {
          displayNotification(`Failed to sync credit note to ${serviceName}`, {
            type: 'error',
            description: getSyncErrorDescription(error)
          })
        },
        onSuccess: () => {
          displayNotification(`Credit note synced to ${serviceName}`, {
            type: 'success'
          })
          /**
           * The external URL doesn't appear immediately, so wait a little while
           * and then bust the cache to ensure we load in the updated data.
           */
          setTimeout(() => {
            runMutationInvalidation(
              dashboard20240730Client.postPushCreditNoteToExternalService
            )
          }, 2000)
        }
      }
    )
  }

  return {
    loading: creditNoteQuery.isPending,
    serviceName,
    creditNote: creditNoteQuery.data ?? null,
    link: {
      disabled: !customerQuery.data || linkingMutation.isPending,
      onClick: linkCreditNote,
      linking: linkingMutation.isPending
    }
  }
}

const SyncUnavailable = (props: {
  reason: SyncUnavailableReason
  serviceName: string
}) => (
  <Flex
    flexDirection="column"
    gap="4px"
    padding="12px"
    backgroundColor={GreyGrey10}
    borderRadius="8px"
  >
    <Box {...Primary13SemiBold} color={GreyGrey80}>
      Credit note
    </Box>
    {match(props.reason)
      .with('notIssued', () => (
        <Box
          {...Primary13Regular}
          color={GreyGrey60}
          data-testid="linkCreditNoteWidget.willSyncWhenFinal"
        >
          This credit note will be synced once it's issued in Sequence.
        </Box>
      ))
      .with('voided', () => (
        <Box
          {...Primary13Regular}
          color={GreyGrey60}
          data-testid="linkCreditNoteWidget.voided"
        >
          This credit note has been voided, so it can't be synced to{' '}
          {props.serviceName}.
        </Box>
      ))
      .exhaustive()}
  </Flex>
)

export const LinkedCreditNoteWidget = (props: LinkedCreditNoteWidgetProps) => {
  const hook = useLinkedCreditNoteWidget(props)

  if (hook.loading) {
    return <Skeleton height="104px" width="100%" />
  }

  if (!hook.creditNote) {
    return null
  }

  if (hook.creditNote.linkedCreditNote) {
    return (
      <ExistingEntityLink
        data-testid={`linkCreditNoteWidget.${props.service}.linked`}
        externalEntityLabel="Credit note"
        serviceName={hook.serviceName}
        unlinkModal={({ onClose }) => (
          <UnlinkCreditNoteModal
            service={props.service}
            creditNoteId={props.creditNoteId}
            onClose={onClose}
          />
        )}
        linkedEntity={{
          label: `ID: ${hook.creditNote.linkedCreditNote.id}`,
          href: hook.creditNote.linkedCreditNote.url,
          canUnlink: true,
          isPending: false
        }}
      />
    )
  }

  if (hook.creditNote.syncUnavailableReason) {
    return (
      <SyncUnavailable
        reason={hook.creditNote.syncUnavailableReason}
        serviceName={hook.serviceName}
      />
    )
  }

  return (
    <Flex
      flexDirection="column"
      gap="4px"
      padding="12px"
      backgroundColor={GreyGrey10}
      borderRadius="8px"
      data-testid={`linkCreditNoteWidget.${props.service}`}
    >
      <Box {...Primary13SemiBold} color={GreyGrey80}>
        Credit note
      </Box>
      <Box {...Primary13Regular} color={GreyGrey60} marginBottom="4px">
        This credit note has not yet been synced to {hook.serviceName}.
      </Box>
      <ProtectedButton permission={'org.creditNote:sync'}>
        <Button
          variant="secondary"
          onClick={hook.link.onClick}
          disabled={hook.link.disabled}
          data-testid={`linkCreditNoteWidget.${props.service}.syncCreditNote`}
        >
          {hook.link.linking ? (
            <Flex gap="8px">
              <Spinner height="16px" width="16px" color={GreyGrey60} />
              Syncing...
            </Flex>
          ) : (
            'Sync credit note'
          )}
        </Button>
      </ProtectedButton>
    </Flex>
  )
}
